import { useMemo } from 'react';
import * as React from 'react';

import { FieldsWithType } from '@lmt/unicorn-shared/app/helpers/typeUtils';

import {
  MultiSelectDropDownInput as MultiSelectDropDownInput2,
  MultiSelectDropDownInputProps,
} from './MultiSelectDropDownInput2';

type Props<
  D,
  V extends FieldsWithType<D, string | number>,
> = MultiSelectDropDownInputProps<D, false> & {
  options: D[];
  idFunction: V;
  getOptionLabel: FieldsWithType<D, string> | ((option: D) => string | number);
  values: Array<D[V]> | undefined | null;
  // If hiddenFunc is specified and returns true for a certain option, then this option will not be shown.
  // Technically it's almost the same as filtering `options`.
  // But `hiddenFunc` helps if there are previously selected values, that you don't want to be selectable anymore (e.g. deleted Dictionary items).
  // In this case you want to show previously selected values as is, but don't want to show them in drop-down.
  hiddenFunc?: (item: D) => boolean;
  rootClassName?: string;
  disabled?: boolean;
  headerTitle?: string;
  allSelectedLabel?: string;
  placeholder?: string;
  errorText?: string;
  variant?: 'normal' | 'formInput';
  postfixRenderer?: (option: D) => React.ReactElement<unknown>;
  hasSearchFilter?: boolean;
};

export function MultiSelectDropDownInput<
  D extends Record<V, string | number>,
  V extends FieldsWithType<D, string | number>,
>(props: Props<D, V>) {
  const { values, onValueChanged, hiddenFunc } = props;

  const values2 = useMemo(() => {
    if (!props.idFunction) return values;
    if (!values || values.length === 0) return [];

    return props.options.filter((option) =>
      values!.includes((option as any)[props.idFunction as any]),
    );
  }, [props.options, values]);

  return (
    <MultiSelectDropDownInput2
      {...props}
      value={values2 as any}
      onValueChanged={onValueChanged as any}
      getOptionDisabled={hiddenFunc}
    />
  );
}
